"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScoApprovalListResponse = exports.ScoApprovalCall = exports.IngestResolvedScoApprovalRequest = exports.ResolveScoApprovalRequest = exports.OpenScoApprovalResponse = exports.OpenScoApprovalRequest = exports.ScoApprovalOutcome = exports.ScoApprovalState = exports.ScoApprovalReason = void 0;
const zod_1 = require("zod");
/**
 * SCO ↔ attendant approval channel (RFL-122) — v1 HTTP long-poll.
 *
 * A self-checkout terminal opens an "approval call" (unknown barcode,
 * age check, weight override, …); an attendant long-polls the open
 * queue, claims a call (first-claim-wins), and resolves it; the SCO
 * polls (or long-polls) for the resolution. WebSocket transport is
 * deferred to v2 (filed separately). All transitions are audit-logged.
 *
 * `callId` is minted by BOS and returned from the open call; it is the
 * stable handle the SCO uses for the resolution poll. Idempotent opens
 * are supported via an optional `Idempotency-Key` header that becomes
 * the `callId`.
 */
exports.ScoApprovalReason = zod_1.z.enum([
    'unknownBarcode',
    'ageVerification',
    'weightOverride',
    'weightTare',
    'manualPriceApproval',
    'customerRequest',
    'loyaltyAssistance',
]);
exports.ScoApprovalState = zod_1.z.enum(['open', 'claimed', 'resolved']);
exports.ScoApprovalOutcome = zod_1.z.enum(['approve', 'reject', 'override']);
/** SCO → BOS: open a call. `terminalId` is echoed for clarity but the
 *  authenticated terminal token is the authoritative source. */
exports.OpenScoApprovalRequest = zod_1.z
    .object({
    terminalId: zod_1.z.string().min(1),
    reason: exports.ScoApprovalReason,
    basketSnapshot: zod_1.z.unknown(),
    lineRef: zod_1.z.string().max(120).optional(),
    ageGroup: zod_1.z.string().max(40).optional(),
})
    .strict();
exports.OpenScoApprovalResponse = zod_1.z.object({
    callId: zod_1.z.string(),
    openedAt: zod_1.z.string().datetime(),
});
/** Attendant → BOS: resolve a claimed call. */
exports.ResolveScoApprovalRequest = zod_1.z
    .object({
    outcome: exports.ScoApprovalOutcome,
    reason: zod_1.z.string().max(1000).optional(),
    attendantCashierId: zod_1.z.string().min(1),
})
    .strict();
/**
 * SCO → BOS: ingest a call that was opened AND resolved on the store
 * LAN, uploaded after the fact for the audit trail (RFL-125 / ADR-0010).
 *
 * Under the LAN-direct topology the live attendant↔SCO supervision is
 * peer-to-peer on the store network — BOS is deliberately NOT in that
 * path, so it never sees `open` / `claimed` for these calls. The SCO
 * records the completed call in one shot once connectivity allows.
 * Idempotent on `callId`; the authenticated SCO terminal is the
 * authoritative `terminalId`. `resolvedByTerminalId` is the attendant
 * device that decided on the LAN (optional — a call may be resolved on
 * the SCO itself, e.g. an attendant badge tap).
 */
exports.IngestResolvedScoApprovalRequest = zod_1.z
    .object({
    callId: zod_1.z.string().min(1).max(120),
    reason: exports.ScoApprovalReason,
    basketSnapshot: zod_1.z.unknown(),
    lineRef: zod_1.z.string().max(120).optional(),
    ageGroup: zod_1.z.string().max(40).optional(),
    outcome: exports.ScoApprovalOutcome,
    resolveReason: zod_1.z.string().max(1000).optional(),
    resolvedByCashierId: zod_1.z.string().min(1),
    resolvedByTerminalId: zod_1.z.string().min(1).optional(),
    openedAt: zod_1.z.string().datetime(),
    resolvedAt: zod_1.z.string().datetime(),
})
    .strict();
/** Read shape returned to attendants (list) and SCO (resolution poll). */
exports.ScoApprovalCall = zod_1.z.object({
    id: zod_1.z.string(),
    callId: zod_1.z.string(),
    terminalId: zod_1.z.string(),
    reason: exports.ScoApprovalReason,
    state: exports.ScoApprovalState,
    basketSnapshot: zod_1.z.unknown(),
    lineRef: zod_1.z.string().nullable(),
    ageGroup: zod_1.z.string().nullable(),
    claimedByUserId: zod_1.z.string().nullable(),
    claimedAt: zod_1.z.string().datetime().nullable(),
    outcome: exports.ScoApprovalOutcome.nullable(),
    resolveReason: zod_1.z.string().nullable(),
    resolvedByCashierId: zod_1.z.string().nullable(),
    resolvedByTerminalId: zod_1.z.string().nullable(),
    openedAt: zod_1.z.string().datetime(),
    resolvedAt: zod_1.z.string().datetime().nullable(),
});
exports.ScoApprovalListResponse = zod_1.z.object({
    items: zod_1.z.array(exports.ScoApprovalCall),
    cursor: zod_1.z.string(),
    hasMore: zod_1.z.boolean(),
});
//# sourceMappingURL=sco-approvals.js.map